import type {
  AdminApiCreditUsage,
  AdminApiUsagePeriod,
} from "@repo/app-types/entities/creditUsage";
import { Size } from "@repo/ui/helpers/helpers";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconCalendar } from "@tabler/icons-react";
import { DateTime } from "luxon";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { formatPeriodLabel } from "./usageFormatting.js";

/**
 * The selected period's usage, and whether it is still arriving. Sections
 * render only once the page has established that the figures are readable,
 * so a failed read has no state here — see {@link BillingSettingsPage}.
 *
 * `currentData` is intentionally argument-specific. RTK Query's `data` falls
 * back to the previous argument's result while a new period loads, which
 * would make stale usage look like it belongs to the selected period.
 */
export function useCreditUsageSection<TUsage extends AdminApiCreditUsage>(
  useUsageQuery: (args: { periodOffset: number }) => {
    currentData: TUsage | undefined;
    isFetching: boolean;
  },
  periodOffset: number,
): { selectedUsage: TUsage | undefined; isUsageLoading: boolean } {
  const { currentData: selectedUsage, isFetching } = useUsageQuery({
    periodOffset,
  });
  return {
    selectedUsage,
    isUsageLoading: isFetching || selectedUsage === undefined,
  };
}

interface CreditUsageHeaderProps {
  /** Periods offered by the picker; hidden until the current period loads. */
  periods: readonly AdminApiUsagePeriod[] | undefined;
  periodOffset: number;
  onPeriodOffsetChange: (periodOffset: number) => void;
}

/**
 * The page-level header shared by every credit section: title and one period
 * picker. Both credit types report the same billing periods, so the AI
 * usage's period list stands for all of them.
 */
export const CreditUsageHeader: FC<CreditUsageHeaderProps> = (props) => {
  const { periods, periodOffset, onPeriodOffsetChange } = props;
  const periodOptions: readonly SimpleSelectOption<number>[] =
    periods?.map((period) => ({
      value: period.offset,
      label: formatPeriodLabel(period),
    })) ?? [];

  return (
    <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
      <h2 className="text-base font-semibold text-foreground">
        Usage and credits
      </h2>
      <div className="flex items-center gap-2">
        {periods ? (
          <SimpleSelect
            isLabelHidden
            label="Usage period"
            leftAccessory={<IconCalendar className="size-4" />}
            onChange={onPeriodOffsetChange}
            options={periodOptions}
            size={Size.SMALL}
            value={periodOffset}
          />
        ) : null}
      </div>
    </div>
  );
};

/** The overview card's placeholder while a period's figures load. */
export const CreditUsageCardSkeleton: FC = () => (
  <div className="rounded-md border border-border-light bg-white p-4">
    <DefaultSkeleton />
  </div>
);

/**
 * Linear projection of a window's final credit total from the fraction of it
 * elapsed. Null while so little has elapsed that the extrapolation would be
 * noise.
 */
export function projectTotal(window: {
  creditsUsed: number;
  startsAt: string;
  endsAt: string;
}): number | null {
  const start = DateTime.fromISO(window.startsAt).toMillis();
  const end = DateTime.fromISO(window.endsAt).toMillis();
  const elapsedFraction = (Date.now() - start) / (end - start);

  if (elapsedFraction < 0.03 || elapsedFraction > 1) {
    return null;
  }
  return window.creditsUsed / elapsedFraction;
}

/** Usage share of the period's credits at which the bar warns. */
const USAGE_ALERT_THRESHOLD_PERCENT = 85;

interface UsageBarProps {
  percentage: number;
  /**
   * Whether the credits this bar measures are gone. A closed period can pass
   * 100% of its included grant with a purchased pack having covered the rest,
   * so the alarm state follows the figure that says so rather than the bar's
   * own fill.
   */
  isExhausted: boolean;
  className?: string;
}

/** Credits consumed against the period's spend capacity. */
export const UsageBar: FC<UsageBarProps> = (props) => {
  const { percentage, isExhausted, className } = props;

  const fillColor = isExhausted
    ? "bg-red-500"
    : percentage >= USAGE_ALERT_THRESHOLD_PERCENT
      ? "bg-amber-500"
      : "bg-indigo-600";

  return (
    <div
      aria-label={`${Math.round(percentage)}% of available credits used`}
      className={twMerge(
        "h-2 overflow-hidden rounded-full bg-gray-100",
        className,
      )}
      role="meter"
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={Math.min(100, Math.round(percentage))}
    >
      <div
        className={twMerge(
          "h-full rounded-full transition-all duration-300",
          fillColor,
        )}
        style={{ width: `${Math.min(100, percentage)}%` }}
      />
    </div>
  );
};
